
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from 'src/components/docs/ContentSwitcher';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'OAuth server setup',
  layout: 'guide',
  description: 'How to configure your OAuth 2.0/OIDC server for Plaid Core Exchange',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='OAuth server setup' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "quick-reference"
      }}>{`Quick reference`}</h2>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Component`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Requirement`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Where to find details`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Discovery endpoint`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/.well-known/openid-configuration`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#configuration-requirements"
              }}>{`Configuration requirements`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`JWKS endpoint`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Public HTTPS endpoint`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#jwks-json-web-key-set"
              }}>{`JWKS section`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Consistency key`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`7+ character unique ID`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#unique-user-identifier-consistency-key"
              }}>{`Unique user identifier`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Credentials`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Client ID + Secret`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#credentials"
              }}>{`Credentials section`}</a></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Required scopes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`openid`}</inlineCode>{`, `}<inlineCode parentName="td">{`offline_access`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "#well-known-configuration"
              }}>{`Well-known configuration`}</a></td>
          </tr>
        </tbody>
      </table>
      <h2 {...{
        "id": "oauth-server-setup"
      }}>{`OAuth server setup`}</h2>
      <p>{`Your OAuth 2.0 server needs to issue standards-compliant tokens. Plaid strongly recommends implementing `}<a parentName="p" {...{
          "href": "https://openid.net/connect/"
        }}>{`OpenID Connect`}</a>{` with the `}<a parentName="p" {...{
          "href": "https://openid.net/specs/openid-connect-discovery-1_0.html"
        }}>{`OIDC Discovery Specification`}</a>{`. OIDC enables a better user experience for `}<a parentName="p" {...{
          "href": "/docs/link/returning-user/"
        }}>{`returning users`}</a>{`, centralized consent management, and optimal API performance through the ID token's `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field. Plain OAuth 2.0 is supported but requires providing the unique user identifier via the `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` endpoint instead.`}</p>
      <p>{`For a working reference you can run locally, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
        }}>{`FDX Explorer`}</a>{` and linked source code on the Authentication overview.`}</p>
      <h3 {...{
        "id": "implementation-options"
      }}>{`Implementation options`}</h3>
      <p><strong parentName="p">{`Use an existing identity provider`}</strong>{` - Okta, Auth0, Ping Identity, Azure AD, or AWS Cognito all work, and provide the fastest implementation path.`}</p>
      <Callout mdxType="Callout">
        <p>{`Plaid recommends `}<a parentName="p" {...{
            "href": "https://www.okta.com/blog/2023/02/how-oktas-new-plaid-integration-keeps-your-banking-secure/"
          }}>{`Okta's Plaid integration`}</a>{`.`}</p>
      </Callout>
      <p><strong parentName="p">{`Build your own`}</strong>{` - Use an `}<a parentName="p" {...{
          "href": "https://openid.net/developers/libraries/"
        }}>{`OIDC-certified library`}</a>{`. This option gives you more control but takes more time.`}</p>
      <Callout warning mdxType="Callout">
        <p>{`Don't roll your own OAuth implementation from scratch. Custom, non-standard implementations create headaches for everyone. Stick to certified tools and established identity providers.`}</p>
      </Callout>
      <h3 {...{
        "id": "configuration-requirements"
      }}>{`Configuration requirements`}</h3>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Configuration`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Notes`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Server domain`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Pick a domain for your OAuth server, usually something like `}<inlineCode parentName="td">{`auth.yourbank.com`}</inlineCode>{` or `}<inlineCode parentName="td">{`yourbank.okta.com`}</inlineCode>{`. Keep it separate from your API server. HTTPS is required.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Discovery endpoint`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid pulls your server config from `}<inlineCode parentName="td">{`/.well-known/openid-configuration`}</inlineCode>{`. If your domain is `}<inlineCode parentName="td">{`https://auth.firstplatypusbank.com`}</inlineCode>{`, Plaid will hit `}<inlineCode parentName="td">{`https://auth.firstplatypusbank.com/.well-known/openid-configuration`}</inlineCode>{`. Make sure it returns the details in the `}<a parentName="td" {...{
                "href": "#well-known-configuration"
              }}>{`next section`}</a>{`, and that it is reachable from the public internet. If the Validator reports `}<inlineCode parentName="td">{`Error getting OIDC configuration`}</inlineCode>{`, see `}<a parentName="td" {...{
                "href": "/core-exchange/docs/troubleshooting#validator-end-to-end-failures"
              }}>{`Validator end-to-end failures`}</a>{`.`}</td>
          </tr>
        </tbody>
      </table>
      <h3 {...{
        "id": "well-known-configuration"
      }}>{`Well-known configuration`}</h3>
      <p>{`Your `}<inlineCode parentName="p">{`/.well-known/openid-configuration`}</inlineCode>{` endpoint is public (no auth required). When Plaid hits it, the response should include at minimum these values:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Property`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Description`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Required`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Type`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`authorization_endpoint`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where users authenticate. This endpoint hosts your login page. Must support TLS and be publicly accessible.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`URL`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`token_endpoint`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where Plaid exchanges authorization codes for access tokens. Backend-to-backend only.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`URL`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`userinfo_endpoint`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Where Plaid retrieves the user identifier. Required for OIDC. Plain OAuth 2.0 implementations that do not use OIDC must instead provide the identifier via `}<inlineCode parentName="td">{`/customers/current`}</inlineCode>{`.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes (OIDC)`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`URL`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`token_endpoint_auth_methods_supported`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`How Plaid authenticates when `}<a parentName="td" {...{
                "href": "/core-exchange/docs/authentication/oauth-flow/#step-4---plaid-requests-an-access-token"
              }}>{`requesting tokens`}</a>{`. If omitted, Plaid defaults to `}<a parentName="td" {...{
                "href": "https://www.rfc-editor.org/rfc/rfc6749#section-2.3.1"
              }}>{`basic authentication`}</a>{`.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Array of strings`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`response_types_supported`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Supported response types. Must include `}<inlineCode parentName="td">{`code`}</inlineCode>{`.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Array of strings`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`scopes_supported`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`For OIDC implementations, must include `}<inlineCode parentName="td">{`openid`}</inlineCode>{` and `}<inlineCode parentName="td">{`offline_access`}</inlineCode>{` at a minimum. Plain OAuth 2.0 only requires `}<inlineCode parentName="td">{`offline_access`}</inlineCode>{`. See details below.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Array of strings`}</td>
          </tr>
        </tbody>
      </table>
      <p><strong parentName="p">{`Scope details:`}</strong></p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`openid`}</inlineCode>{` - Enables OIDC features. Lets Plaid read the user identifier from the ID token, enabling returning user experience, optimized API traffic, and centralized consent management. (If this scope is unavailable, Plaid will query `}<inlineCode parentName="li">{`/customers/current`}</inlineCode>{` instead, but OIDC-specific features won't be available.)`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`offline_access`}</inlineCode>{` - Gives Plaid a refresh token to fetch data in the background. Required for use cases that need data access beyond the initial OAuth session, such as balance checks for stored bank accounts (recurring transfers, saved payment methods), transaction subscriptions, and ongoing data updates.`}</li>
      </ul>
      <p><strong parentName="p">{`FDX scopes:`}</strong>{` Plaid may also request FDX data-scope values in the `}<inlineCode parentName="p">{`scope`}</inlineCode>{` parameter (for example `}<inlineCode parentName="p">{`fdx:accountbasic:read`}</inlineCode>{` or `}<inlineCode parentName="p">{`fdx:transactions:read`}</inlineCode>{`) to describe the categories of data it's accessing:`}</p>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Scope`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Data category`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:accountbasic:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Basic account information`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:accountdetailed:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Detailed account information`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:paymentsupport:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Full account number and payment network routing number`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:customercontact:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Customer contact information`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:investments:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Investment holdings and transactions`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:statements:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Account statements`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fdx:transactions:read`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Transaction history`}</td>
          </tr>
        </tbody>
      </table>
      <p>{`FDX deprecated `}<inlineCode parentName="p">{`fdx:accountpayments:read`}</inlineCode>{` in favor of `}<inlineCode parentName="p">{`fdx:paymentsupport:read`}</inlineCode>{` in FDX 6.1.0, and will remove it in FDX 7. Plaid requests `}<inlineCode parentName="p">{`fdx:paymentsupport:read`}</inlineCode>{`.`}</p>
      <p>{`Your OAuth server does not need to enforce access control per FDX scope. Plaid requests them descriptively, and it's fine to grant a token every FDX scope your implementation supports without gating individual endpoints on them.`}</p>
      <h4 {...{
        "id": "example"
      }}>{`Example`}</h4>
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "# Plaid's request",
          "#": true,
          "Plaid's": true,
          "request": true
        }}>{`curl -X GET 'https://auth.firstplatypusbank.com/.well-known/openid-configuration'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Your response",
          "#": true,
          "Your": true,
          "response": true
        }}>{`{
  "authorization_endpoint": "https://auth.firstplatypusbank.com/oauth2/v1/authorize",
  "token_endpoint": "https://auth.firstplatypusbank.com/oauth2/v1/token",
  "userinfo_endpoint": "https://auth.firstplatypusbank.com/oauth2/v1/userinfo",
  "token_endpoint_auth_methods_supported": ["client_secret_basic"],
  "response_types_supported": ["code"],
  "scopes_supported": ["openid", "offline_access"]
}
`}</code></pre>
      <h4 {...{
        "id": "what-to-validate"
      }}>{`What to validate`}</h4>
      <p>{`When Plaid (or your own tooling) fetches your well-known configuration, confirm:`}</p>
      <ul>
        <li parentName="ul">{`All endpoint URLs use HTTPS, resolve via DNS, and are reachable from the public internet with valid Production TLS certificates.`}</li>
        <li parentName="ul">{`The endpoint returns HTTP 200 with `}<inlineCode parentName="li">{`Content-Type: application/json`}</inlineCode>{` and well-formed JSON.`}</li>
        <li parentName="ul">{`Required properties in the table above are present and correctly populated (including `}<inlineCode parentName="li">{`authorization_endpoint`}</inlineCode>{`, `}<inlineCode parentName="li">{`token_endpoint`}</inlineCode>{`, `}<inlineCode parentName="li">{`response_types_supported`}</inlineCode>{` with `}<inlineCode parentName="li">{`code`}</inlineCode>{`, and `}<inlineCode parentName="li">{`scopes_supported`}</inlineCode>{` with `}<inlineCode parentName="li">{`offline_access`}</inlineCode>{` and, for OIDC, `}<inlineCode parentName="li">{`openid`}</inlineCode>{`).`}</li>
        <li parentName="ul">{`For OIDC, `}<inlineCode parentName="li">{`jwks_uri`}</inlineCode>{` is present, publicly accessible, and returns at least one signing key in a valid JWKS document.`}</li>
      </ul>
      <p><strong parentName="p">{`Note for multi-institution deployments:`}</strong>{` If you serve multiple financial institutions, the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field in the ID token must be unique per institution (but doesn't need to be unique across all institutions).`}</p>
      <h2 {...{
        "id": "unique-user-identifier-consistency-key"
      }}>{`Unique user identifier (consistency key)`}</h2>
      <p>{`Plaid needs a stable identifier for each user, something that never changes, is unique across your user base, and stays consistent when that user connects multiple apps. This identifier (often called the "consistency key") enables Plaid to:`}</p>
      <ul>
        <li parentName="ul">{`Show users all their active connections in one place (my.plaid.com)`}</li>
        <li parentName="ul">{`Maintain a user-level view in `}<a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`Consent management`}</a></li>
        <li parentName="ul">{`Optimize API traffic by recognizing returning users`}</li>
        <li parentName="ul">{`Enable the returning user experience`}</li>
      </ul>
      <h3 {...{
        "id": "requirements"
      }}>{`Requirements`}</h3>
      <p>{`The identifier must be:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Stable`}</strong>{` - Never changes for the duration of the user's relationship with you`}</li>
        <li parentName="ul"><strong parentName="li">{`Unique`}</strong>{` - No two users share the same identifier`}</li>
        <li parentName="ul"><strong parentName="li">{`Consistent`}</strong>{` - Same value across all apps a user connects to via Plaid`}</li>
        <li parentName="ul"><strong parentName="li">{`Non-sensitive`}</strong>{` - Should not expose personally identifiable information (avoid SSN, email, phone)`}</li>
        <li parentName="ul"><strong parentName="li">{`Sufficient length`}</strong>{` - Minimum seven characters`}</li>
      </ul>
      <p>{`In practice, the best option is usually an internal customer identifier from your core system (for example, a GUID or numeric customer ID) as long as it is never reused, never changes, and does not expose login names or account numbers. Avoid using online banking usernames, email addresses, phone numbers, or account numbers as the identifier.`}</p>
      <h3 {...{
        "id": "how-to-provide-it"
      }}>{`How to provide it`}</h3>
      <p>{`You have three options. Plaid tries them in this order and uses the first one available:`}</p>
      <ol>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`sub`}</inlineCode>{` field in the ID token (recommended)`}</strong>{` - Include the user identifier as the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` claim when issuing the `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` during token exchange. Plaid uses this whenever you return an `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` `}<strong parentName="p">{`and`}</strong>{` publish a `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{`, since it needs the key set to verify the token's signature. No extra request is needed beyond fetching your keys, which Plaid caches.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`userinfo`}</inlineCode>{` endpoint`}</strong>{` - Return the identifier as the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field in the response from your `}<inlineCode parentName="p">{`userinfo_endpoint`}</inlineCode>{`. Plaid uses this when there's no ID token or no `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{`, and it costs one additional request to your authorization server per login.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p"><inlineCode parentName="strong">{`/customers/current`}</inlineCode>{` endpoint`}</strong>{` - Return the identifier as `}<inlineCode parentName="p">{`customerId`}</inlineCode>{` in the Core Exchange `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` response. Plaid uses this only when neither of the above is available. It's the least preferred option because it isn't part of OIDC, it makes login depend on your FDX API being reachable, and it forgoes the OIDC features described above.`}</p>
        </li>
      </ol>
      <p>{`Most integrations use option 1. Including the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field in the ID token is the standard OIDC approach and requires no additional endpoint calls.`}</p>
      <p>{`Selection happens once, based on what you've configured, and does not use a fallback flow. For example, publishing a `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` and returning an `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` commits you to option 1. If Plaid can't verify your `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` against your JWKS, it reports that verification error and stops; it will not fall back to options 2 or 3. So a login failing in an integration that publishes a `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` indicates an error with your keys or your token signing, not with the `}<inlineCode parentName="p">{`userinfo`}</inlineCode>{` or `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` endpoints.`}</p>
      <p>{`Whichever option you use, the value must satisfy the requirements above. A value shorter than seven characters, or absent entirely, fails the login with an invalid consistency key error rather than falling through to another option.`}</p>
      <h3 {...{
        "id": "example-1"
      }}>{`Example`}</h3>
      <p>{`When Plaid requests a token, your `}<inlineCode parentName="p">{`/token`}</inlineCode>{` endpoint responds with an `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` that includes:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Token response example",
          "#": true,
          "Token": true,
          "response": true,
          "example": true
        }}>{`{
  "access_token": "agstynmdygjdghabrgraeh...",
  "id_token": "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...",
  "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu..."
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Decoded ID token payload example",
          "#": true,
          "Decoded": true,
          "ID": true,
          "token": true,
          "payload": true,
          "example": true
        }}>{`{
  "sub": "user_12345678",
  "iss": "https://auth.firstplatypusbank.com",
  "aud": "dc1fe34ae9e5e98147f2fd76060016a4",
  "exp": 1672444800,
  "iat": 1672441200
}
`}</code></pre>
      <p>{`The `}<inlineCode parentName="p">{`sub`}</inlineCode>{` value (`}<inlineCode parentName="p">{`user_12345678`}</inlineCode>{`) is the consistency key. It's 13 characters, unique to this user, and will never change.`}</p>
      <h2 {...{
        "id": "jwks-json-web-key-set"
      }}>{`JWKS (JSON Web Key Set)`}</h2>
      <Callout mdxType="Callout">
        <p>{`Only OIDC implementations that issue ID tokens need to expose a JWKS endpoint. Plain OAuth 2.0 implementations don't need to provide one.`}</p>
      </Callout>
      <p>{`JWKS is how Plaid verifies your tokens are valid. The `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{` points to your public keys. Plaid uses them to verify the digital signatures on JWTs, such as the `}<inlineCode parentName="p">{`id_token`}</inlineCode>{`. Plaid automatically fetches and caches your JWKS.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "# Example JWKS response",
          "#": true,
          "Example": true,
          "JWKS": true,
          "response": true
        }}>{`{
  "keys": [
    {
      "kty": "RSA",
      "use": "sig",
      "kid": "ABCD1234",
      "n": "4MZuI7r3F5y...",
      "e": "AQAB",
      "alg": "RS256"
    }
  ]
}
`}</code></pre>
      <p><strong parentName="p">{`Keep your JWKS healthy:`}</strong></p>
      <ul>
        <li parentName="ul">{`Host it over HTTPS (non-negotiable).`}</li>
        <li parentName="ul">{`Rotate signing keys at least annually, or immediately if compromised.`}</li>
        <li parentName="ul">{`Include a `}<inlineCode parentName="li">{`kid`}</inlineCode>{` (Key ID) in every key for quick lookup.`}</li>
        <li parentName="ul">{`Use strong algorithms: RSA-2048 or better (`}<inlineCode parentName="li">{`RS256`}</inlineCode>{`, `}<inlineCode parentName="li">{`ES256`}</inlineCode>{`, etc.).`}</li>
      </ul>
      <h3 {...{
        "id": "key-rotation-process"
      }}>{`Key rotation process`}</h3>
      <p>{`Rotating JWKS keys without breaking Plaid's token verification:`}</p>
      <ol>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p">{`Publish the new key`}</strong>{` - Add your new key to the JWKS with a unique `}<inlineCode parentName="p">{`kid`}</inlineCode>{`. Keep the old key in the response.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p">{`Wait for cache refresh`}</strong>{` - Plaid caches JWKS for up to 10 minutes to reduce load on your endpoint. Wait at least that long before proceeding to ensure Plaid has fetched and cached the new keys.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p">{`Start signing with new key`}</strong>{` - Update your ID tokens to use the new `}<inlineCode parentName="p">{`kid`}</inlineCode>{` in the JWT header. Plaid will use the cached JWKS to verify tokens signed with either the old or new key.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p">{`Monitor for errors`}</strong>{` - Watch for signature validation errors (`}<inlineCode parentName="p">{`invalid_signature`}</inlineCode>{`, `}<inlineCode parentName="p">{`kid_not_found`}</inlineCode>{`) in your logs and Plaid's error reports. Monitor for a period that covers your typical traffic patterns before proceeding.`}</p>
        </li>
        <li parentName="ol">
          <p parentName="li"><strong parentName="p">{`Remove the old key`}</strong>{` - After confirming no errors over a reasonable monitoring period, remove the old key from your JWKS. Plaid will remove the old key from its cache the next time it fetches your JWKS (within 10 minutes).`}</p>
        </li>
      </ol>
      <p><strong parentName="p">{`JWKS caching behavior:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Cache duration:`}</strong>{` Up to 10 minutes`}</li>
        <li parentName="ul"><strong parentName="li">{`Fetch trigger:`}</strong>{` Cache miss or expiration`}</li>
        <li parentName="ul"><strong parentName="li">{`During rotation:`}</strong>{` Both old and new keys remain cached and valid during the transition period`}</li>
      </ul>
      <p><strong parentName="p">{`Emergency rotation:`}</strong>{` If a key is compromised, rotate immediately. Coordinate with your Plaid contact to minimize disruption. Be prepared for some token validation failures during emergency rotations.`}</p>
      <p><strong parentName="p">{`Recommended schedule:`}</strong>{` Annually at a minimum. Schedule rotations during low-traffic periods when possible.`}</p>
      <h2 {...{
        "id": "credentials"
      }}>{`Credentials`}</h2>
      <p>{`Plaid needs credentials to authenticate when requesting access tokens. You'll issue us a client ID (public identifier) and client secret (password).`}</p>
      <h3 {...{
        "id": "client-id"
      }}>{`Client ID`}</h3>
      <p>{`The client ID is public, but don't make it easy to guess. Length: 8-256 characters.`}</p>
      <h4 {...{
        "id": "how-to-create-one"
      }}>{`How to create one`}</h4>
      <p>{`Generate a random string using your language's cryptographically secure random number generator (CSPRNG), then encode as hexadecimal. Target 16 random bytes, which produces a 32-character hex string.`}</p>
      <ContentSwitcher.Content>
        <MDXCodeBlock title="Generate a random client ID" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`openssl rand -hex 16
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`import crypto from 'crypto';
const randomString = crypto.randomBytes(16).toString('hex');
console.log(randomString);
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`import secrets
secrets.token_hex(16)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`import java.security.SecureRandom;

public class RandomHex {
    public static void main(String[] args) {
        SecureRandom secureRandom = new SecureRandom();
        byte[] randomBytes = new byte[16];
        secureRandom.nextBytes(randomBytes);

        StringBuilder hexString = new StringBuilder();
        for (byte b : randomBytes) {
            hexString.append(String.format("%02x", b));
        }

        System.out.println(hexString.toString());
    }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`require 'securerandom'

random_string = SecureRandom.hex(16)
puts random_string
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`package main

import (
    "crypto/rand"
    "encoding/hex"
    "fmt"
    "log"
)

func main() {
    randomBytes := make([]byte, 16)
    _, err := rand.Read(randomBytes)
    if err != nil {
        log.Fatal(err)
    }

    randomString := hex.EncodeToString(randomBytes)
    fmt.Println(randomString)
}
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <h3 {...{
        "id": "client-secret"
      }}>{`Client secret`}</h3>
      <p>{`The client secret is Plaid's password. Treat it like one. Follow these `}<a parentName="p" {...{
          "href": "https://www.oauth.com/oauth2-servers/client-registration/client-id-secret/"
        }}>{`security best practices`}</a>{`:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Generate it securely`}</strong>{` - Use a CSPRNG (cryptographically secure pseudo random number generator). Don't use UUIDs, as many libraries leak timestamps or MAC addresses.`}</li>
        <li parentName="ul"><strong parentName="li">{`Never store it in plain text`}</strong>{` - Encrypt or hash it. Always.`}</li>
        <li parentName="ul"><strong parentName="li">{`Make it visually distinct`}</strong>{` - Different format from the client ID helps prevent copy/paste errors.`}</li>
      </ul>
      <p>{`Length: 8-256 characters.`}</p>
      <h4 {...{
        "id": "how-to-generate-one"
      }}>{`How to generate one`}</h4>
      <p>{`Generate a 256-bit value using your language's CSPRNG, then encode as hexadecimal. This produces a 64-character hex string.`}</p>
      <ContentSwitcher.Content>
        <MDXCodeBlock title="Generate a random client secret" mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`openssl rand -hex 32
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-node"
            }}>{`import crypto from 'crypto';
const randomString = crypto.randomBytes(32).toString('hex');
console.log(randomString);
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python"
            }}>{`import secrets
secrets.token_hex(32)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java"
            }}>{`import java.security.SecureRandom;

public class RandomHex {
  public static void main(String[] args) {
    SecureRandom secureRandom = new SecureRandom();
    byte[] randomBytes = new byte[32];
    secureRandom.nextBytes(randomBytes);

    StringBuilder hexString = new StringBuilder();
    for (byte b : randomBytes) {
      hexString.append(String.format("%02x", b));
    }

    System.out.println(hexString.toString());
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby"
            }}>{`require 'securerandom'

random_string = SecureRandom.hex(32)
puts random_string
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go"
            }}>{`package main

import (
    "crypto/rand"
    "encoding/hex"
    "fmt"
    "log"
)

func main() {
    randomBytes := make([]byte, 32)
    _, err := rand.Read(randomBytes)
    if err != nil {
        log.Fatal(err)
    }

    randomString := hex.EncodeToString(randomBytes)
    fmt.Println(randomString)
}
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
      <h2 {...{
        "id": "token-expiration"
      }}>{`Token expiration`}</h2>
      <p>{`Configure your token lifetimes to balance security with usability.`}</p>
      <h3 {...{
        "id": "access-tokens-15-minutes"
      }}>{`Access tokens: 15 minutes`}</h3>
      <p>{`Short-lived access tokens limit exposure if a token is compromised. Plaid checks expiration before each API call and automatically refreshes when needed.`}</p>
      <h3 {...{
        "id": "refresh-tokens-13-months"
      }}>{`Refresh tokens: 13+ months`}</h3>
      <p>{`Compliance requires users to reauthorize every 12 months, which Plaid handles automatically. Rotating refresh tokens or setting their expiration to 13+ months ensures they remain valid through this cycle. The extra buffer (13+ months is common) accounts for high-traffic periods like tax season, when users may not reauthorize immediately.`}</p>
      <h2 {...{
        "id": "setup-checklist"
      }}>{`Setup checklist`}</h2>
      <ul>
        <li parentName="ul">{`OAuth server configured with OIDC support`}</li>
        <li parentName="ul">{`Discovery endpoint returns valid configuration`}</li>
        <li parentName="ul">{`JWKS endpoint publicly accessible with valid keys`}</li>
        <li parentName="ul">{`Client ID and secret generated securely`}</li>
        <li parentName="ul">{`Consistency key implemented (via `}<inlineCode parentName="li">{`sub`}</inlineCode>{`, userinfo, or `}<inlineCode parentName="li">{`/customers/current`}</inlineCode>{`)`}</li>
        <li parentName="ul">{`TLS certificates valid (not self-signed for Production)`}</li>
        <li parentName="ul">{`Token expiration configured (15 min for access, 13+ months for refresh)`}</li>
        <li parentName="ul">{`All endpoints are tested and responding correctly`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "quick-reference",
        level: 2,
        title: "Quick reference",
      },
{
        id: "oauth-server-setup",
        level: 2,
        title: "OAuth server setup",
      },
{
        id: "implementation-options",
        level: 3,
        title: "Implementation options",
      },
{
        id: "configuration-requirements",
        level: 3,
        title: "Configuration requirements",
      },
{
        id: "well-known-configuration",
        level: 3,
        title: "Well-known configuration",
      },
{
        id: "example",
        level: 4,
        title: "Example",
      },
{
        id: "what-to-validate",
        level: 4,
        title: "What to validate",
      },
{
        id: "unique-user-identifier-consistency-key",
        level: 2,
        title: "Unique user identifier (consistency key)",
      },
{
        id: "requirements",
        level: 3,
        title: "Requirements",
      },
{
        id: "how-to-provide-it",
        level: 3,
        title: "How to provide it",
      },
{
        id: "example-1",
        level: 3,
        title: "Example",
      },
{
        id: "jwks-json-web-key-set",
        level: 2,
        title: "JWKS (JSON Web Key Set)",
      },
{
        id: "key-rotation-process",
        level: 3,
        title: "Key rotation process",
      },
{
        id: "credentials",
        level: 2,
        title: "Credentials",
      },
{
        id: "client-id",
        level: 3,
        title: "Client ID",
      },
{
        id: "how-to-create-one",
        level: 4,
        title: "How to create one",
      },
{
        id: "client-secret",
        level: 3,
        title: "Client secret",
      },
{
        id: "how-to-generate-one",
        level: 4,
        title: "How to generate one",
      },
{
        id: "token-expiration",
        level: 2,
        title: "Token expiration",
      },
{
        id: "access-tokens-15-minutes",
        level: 3,
        title: "Access tokens: 15 minutes",
      },
{
        id: "refresh-tokens-13-months",
        level: 3,
        title: "Refresh tokens: 13+ months",
      },
{
        id: "setup-checklist",
        level: 2,
        title: "Setup checklist",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
